import { useTranslator } from '@/hooks/useTranslator'
import { useEffect } from 'react'
import { Grid, Typography, Button } from '@mui/material'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { FormInput } from '../../form'
import InputDateField from '../../common/input/InputDateField'
import PhoneCodeSelection from '../../selection/PhoneCodeSelection'
import { yupResolver } from '@hookform/resolvers/yup'
import { getCreateUserSchema, IHttpError, IUser, User } from '@/@data'
import { useNotification } from '@/hooks'
import CompanySelection from '../../selection/CompanySelection'
import RoleSelection from '@/components/selection/RoleSelection'
import { HttpStatusCode } from 'axios'

export const CreateUserForm = ({ onClose, refetch }: { onClose: () => void; refetch?: () => void }) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const { useCreateMutation } = User.objects.getCommonMutations()
  const createUserMutation = useCreateMutation()

  const methods = useForm<any>({
    defaultValues: {
      company_id: '',
      first_name: '',
      phone_code: '',
      phone: '',
      birth_date: '',
      email: '',
      employee_id: '',
      role_id: 0
    },
    resolver: yupResolver(getCreateUserSchema(__)),
    mode: 'onChange'
  })

  const companyId = methods.watch('company_id')

  useEffect(() => {
    if (companyId !== 1) {
      methods.setValue('role_id', 4, { shouldValidate: true, shouldDirty: true })
    } else {
      methods.setValue('role_id', null, { shouldValidate: true, shouldDirty: true })
    }
  }, [companyId])

  const onSubmit = (data: IUser) => {
    const payload = {
      ...data
    }

    createUserMutation.mutate(payload, {
      onSuccess: ({ data, statusCode }) => {
        if (statusCode === HttpStatusCode.Conflict && data && Object.keys(data).length) {
          const errors = data as unknown as Record<string, string | undefined>

          for (const element in errors) {
            const message = errors[element]

            if (message && element === 'is_valid_email') {
              methods.setError('email', { message: __('Email is already in use') })
            } else if (message && element === 'is_valid_employee') {
              methods.setError('employee_id', { message: __('Employee code is already in use') })
            }
          }

          return
        }

        n.success(__("You've successfully create new user"))
        onClose()
        refetch?.()
      },
      onError: (error: IHttpError) => {
        n.error(error.message)
      }
    })
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Grid container spacing={4}>
          <Grid size={12}>
            <Controller
              name='company_id'
              control={methods.control}
              render={({ field }) => (
                <CompanySelection
                  {...field}
                  fullWidth
                  required
                  label={__('Company')}
                  onClear={() => methods.setValue('company_id', 0)}
                />
              )}
            />
          </Grid>

          <Grid size={12}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Full Name')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput name='first_name' placeholder={__('Full Name')} control={methods.control} fullWidth />
          </Grid>

          <Grid size={12}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('User Code')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput name='employee_id' placeholder={__('Enter User Code')} control={methods.control} fullWidth />
          </Grid>

          <Grid size={12}>
            <div className='text-[15px] font-medium leading-[15px] mb-2 text-[#2f2b3de6]'>
              {__('Email')} <span className='text-[#FF4C51]'>*</span>
            </div>
            <FormInput name='email' placeholder={__('Email')} control={methods.control} fullWidth />
          </Grid>

          <Grid size={12}>
            <Typography variant='body2' fontWeight={500}>
              {}
            </Typography>
            <Controller
              name='birth_date'
              control={methods.control}
              render={({ field }) => (
                <InputDateField
                  value={field.value}
                  placeholder={__('Select Date')}
                  onChange={field.onChange}
                  className='rounded-full py-2'
                  label={__('D.O.B')}
                  required
                />
              )}
            />
          </Grid>

          <Grid size={12} gap={2}>
            <Grid size={{ xs: 12, sm: 12 }}>
              <Typography variant='body2' fontWeight={500}>
                {__('Phone')}
                {/* <span style={{ color: 'red' }}>*</span> */}
              </Typography>
            </Grid>
            <Grid container sx={{ display: 'flex', flexDirection: 'row', gap: 2, width: '100%' }}>
              <Grid size={{ xs: 12, sm: 4 }} sx={{ flex: 1 }}>
                <Controller
                  name='phone_code'
                  control={methods.control}
                  render={({ field }) => <PhoneCodeSelection {...field} placeholder={__('+1 (*)')} />}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 7 }} sx={{ flex: 1 }} className='flex-1'>
                <FormInput
                  name='phone'
                  placeholder={__('Enter Phone')}
                  type='number'
                  control={methods.control}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid size={12} gap={2}>
            <Typography variant='body2' fontWeight={500}>
              {__('Role')} {companyId === 1 ? <span style={{ color: 'red' }}>*</span> : null}
            </Typography>
            <Controller
              name='role_id'
              control={methods.control}
              render={({ field }) => (
                <RoleSelection
                  {...field}
                  placeholder={__('Select Role')}
                  required={companyId === 1}
                  disabled={companyId !== 1}
                  companyId={companyId}
                />
              )}
            />
          </Grid>
        </Grid>

        <div className='flex flex-row gap-4 mt-8'>
          <Button
            variant='contained'
            className='text-white text-[15px] font-medium leading-[22px] bg-[#3551F2] shadow-none rounded-full'
            type='submit'
            //disabled={!methods.formState.isValid}
          >
            {methods.formState.isSubmitting ? __('Waiting...') : __('Submit')}
          </Button>
          <Button
            variant='contained'
            className='text-[#FF4C51] text-[15px] font-medium leading-[22px] bg-[#FCE2E3] shadow-none rounded-full'
            onClick={onClose}
          >
            {__('Cancel')}
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}
